import { COORDINATE_QUANTUM_MM } from '@bw/core';
import type { SketchPoint, SketchShape } from '@bw/core';
import type { Wall } from '@bw/document';
import type { BuildingOpening } from './types.js';

/**
 * Two quanta. A ring built by the sketch tool lands on the 0.125 mm lattice,
 * so anything meant to be square is square to within rounding; this is loose
 * enough to forgive that and far too tight to let a deliberate angle through.
 */
const SQUARE_TOLERANCE_MM = COORDINATE_QUANTUM_MM * 2;

/**
 * Is this ring an axis-aligned rectangle?
 *
 * Only a rectangle can carry generated joinery: a frame, a sill and a reveal
 * are built from a width and a height, and there is nothing sensible to build
 * inside an arbitrary polygon. Everything else stays an honest hole, which is
 * the phase 2 decision recorded rather than a limitation discovered later.
 */
export function isAxisAlignedRectangle(points: readonly SketchPoint[]): boolean {
  if (points.length !== 4) return false;
  let horizontal = 0;
  for (let i = 0; i < 4; i++) {
    const a = points[i] as SketchPoint;
    const b = points[(i + 1) % 4] as SketchPoint;
    const flat = Math.abs(a.y - b.y) <= SQUARE_TOLERANCE_MM;
    const upright = Math.abs(a.x - b.x) <= SQUARE_TOLERANCE_MM;
    // A degenerate edge is both, and a rectangle has no degenerate edges.
    if (flat === upright) return false;
    if (flat) horizontal += 1;
    // Opposite edges must share an orientation, which for a four-sided ring
    // is the same as saying consecutive edges alternate.
    const previousFlat =
      Math.abs((points[(i + 3) % 4] as SketchPoint).y - a.y) <= SQUARE_TOLERANCE_MM;
    if (i > 0 && previousFlat === flat) return false;
  }
  return horizontal === 2;
}

/** The elevation-frame bounding box of a ring. */
function boundsOf(points: readonly SketchPoint[]): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/**
 * The holes in one wall.
 *
 * Sketch shapes are the source of truth for openings (phase 2 decision), so
 * this reads the wall's own sketch and nothing else. The `openings` array on
 * the document is legacy: still parsed for files that carry it, never written,
 * and deliberately not consulted here — two sources would drift.
 */
export function openingsForWall(wall: Wall): BuildingOpening[] {
  const out: BuildingOpening[] = [];
  for (const shape of wall.model.sketch.shapes) {
    if (shape.role !== 'window' && shape.role !== 'door') continue;
    if (shape.points.length < 3) continue;
    out.push(describe(wall.id, shape, shape.role));
  }
  return out;
}

function describe(
  wallId: string,
  shape: SketchShape,
  role: 'window' | 'door',
): BuildingOpening {
  const box = boundsOf(shape.points);
  return {
    wallId,
    shapeId: shape.id,
    role,
    x: box.x,
    y: box.y,
    width: box.width,
    height: box.height,
    rectangular: isAxisAlignedRectangle(shape.points),
    points: shape.points,
  };
}
